// INN-4: Live Design Token Preview — real-time CSS variable editing
// Click any color swatch to open picker; all components using var(--color-*) update instantly
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Palette, Type, Space, Layers, RotateCcw } from "lucide-react";
import { normalizeColorInputValue } from "./color-input-value";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TokenDefinition {
  readonly variable: string;
  readonly label: string;
  readonly group: TokenGroup;
  readonly type: "color" | "size" | "font" | "shadow";
}

type TokenGroup = "colors" | "spacing" | "typography" | "shadows";

interface TokenSnapshot {
  readonly variable: string;
  readonly value: string;
}

interface LiveTokenPreviewProps {
  tokenValues?: Readonly<Record<string, string>>;
  onTokenChange?: (token: string, value: string) => void;
  onResetAll?: () => void;
}

// ---------------------------------------------------------------------------
// Token catalog — single source of truth for what we expose
// ---------------------------------------------------------------------------

const TOKEN_CATALOG: readonly TokenDefinition[] = [
  // Colors
  { variable: "--color-primary", label: "Primary", group: "colors", type: "color" },
  { variable: "--color-primary-hover", label: "Primary Hover", group: "colors", type: "color" },
  { variable: "--color-primary-light", label: "Primary Light", group: "colors", type: "color" },
  { variable: "--color-primary-border", label: "Primary Border", group: "colors", type: "color" },
  { variable: "--color-secondary", label: "Secondary", group: "colors", type: "color" },
  { variable: "--color-ai", label: "AI Purple", group: "colors", type: "color" },
  { variable: "--color-ai-light", label: "AI Light", group: "colors", type: "color" },
  { variable: "--color-accent", label: "Accent", group: "colors", type: "color" },
  { variable: "--color-accent-action", label: "Action Orange", group: "colors", type: "color" },
  { variable: "--color-success", label: "Success", group: "colors", type: "color" },
  { variable: "--color-warning", label: "Warning", group: "colors", type: "color" },
  { variable: "--color-destructive", label: "Destructive", group: "colors", type: "color" },
  { variable: "--color-info", label: "Info", group: "colors", type: "color" },
  { variable: "--color-background", label: "Background", group: "colors", type: "color" },
  { variable: "--color-surface", label: "Surface", group: "colors", type: "color" },
  { variable: "--color-foreground", label: "Foreground", group: "colors", type: "color" },
  { variable: "--color-muted", label: "Muted", group: "colors", type: "color" },
  { variable: "--color-muted-foreground", label: "Muted FG", group: "colors", type: "color" },
  { variable: "--color-border", label: "Border", group: "colors", type: "color" },
  { variable: "--color-card", label: "Card", group: "colors", type: "color" },
  { variable: "--color-ring", label: "Ring", group: "colors", type: "color" },

  // Spacing
  { variable: "--space-1", label: "1 (4px)", group: "spacing", type: "size" },
  { variable: "--space-2", label: "2 (8px)", group: "spacing", type: "size" },
  { variable: "--space-3", label: "3 (12px)", group: "spacing", type: "size" },
  { variable: "--space-4", label: "4 (16px)", group: "spacing", type: "size" },
  { variable: "--space-6", label: "6 (24px)", group: "spacing", type: "size" },
  { variable: "--space-8", label: "8 (32px)", group: "spacing", type: "size" },
  { variable: "--space-12", label: "12 (48px)", group: "spacing", type: "size" },
  { variable: "--space-16", label: "16 (64px)", group: "spacing", type: "size" },

  // Typography
  { variable: "--font-sans", label: "Sans", group: "typography", type: "font" },
  { variable: "--font-mono", label: "Mono", group: "typography", type: "font" },
  { variable: "--text-xs", label: "XS", group: "typography", type: "size" },
  { variable: "--text-sm", label: "SM", group: "typography", type: "size" },
  { variable: "--text-base", label: "Base", group: "typography", type: "size" },
  { variable: "--text-lg", label: "LG", group: "typography", type: "size" },
  { variable: "--text-xl", label: "XL", group: "typography", type: "size" },
  { variable: "--text-2xl", label: "2XL", group: "typography", type: "size" },
  { variable: "--text-3xl", label: "3XL", group: "typography", type: "size" },
  { variable: "--text-4xl", label: "4XL", group: "typography", type: "size" },

  // Shadows
  { variable: "--shadow-xs", label: "XS", group: "shadows", type: "shadow" },
  { variable: "--shadow-sm", label: "SM", group: "shadows", type: "shadow" },
  { variable: "--shadow-md", label: "MD", group: "shadows", type: "shadow" },
  { variable: "--shadow-lg", label: "LG", group: "shadows", type: "shadow" },
  { variable: "--shadow-xl", label: "XL", group: "shadows", type: "shadow" },
  { variable: "--shadow-glow", label: "Glow", group: "shadows", type: "shadow" },
  { variable: "--shadow-cta", label: "CTA", group: "shadows", type: "shadow" },
] as const;

const GROUP_META: Record<TokenGroup, { label: string; icon: typeof Palette }> = {
  colors: { label: "Colors", icon: Palette },
  spacing: { label: "Spacing", icon: Space },
  typography: { label: "Typography", icon: Type },
  shadows: { label: "Shadows", icon: Layers },
};

const GROUP_ORDER: readonly TokenGroup[] = ["colors", "spacing", "typography", "shadows"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getComputedTokenValue(variable: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface TokenRowProps {
  definition: TokenDefinition;
  currentValue: string;
  defaultValue: string;
  isModified: boolean;
  onValueChange: (variable: string, value: string) => void;
  onReset: (variable: string) => void;
}

function TokenRow({ definition, currentValue, defaultValue, isModified, onValueChange, onReset }: TokenRowProps) {
  const inputId = useId();

  return (
    <div className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-[var(--color-surface)] transition-colors"
         style={{ transitionDuration: "var(--duration-fast)" }}>
      {/* Visual preview */}
      {definition.type === "color" && (
        <label htmlFor={inputId} className="relative shrink-0 cursor-pointer">
          <span
            className="block w-7 h-7 rounded-md border border-[var(--color-border)]"
            style={{ backgroundColor: currentValue }}
          />
          <input
            id={inputId}
            type="color"
            value={normalizeColorInputValue(currentValue)}
            onChange={(e) => onValueChange(definition.variable, e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            aria-label={`Pick color for ${definition.label}`}
          />
        </label>
      )}

      {definition.type === "size" && (
        <span
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]"
        >
          <span
            className="block rounded-sm bg-[var(--color-primary)]"
            style={{ width: currentValue, height: currentValue, maxWidth: "20px", maxHeight: "20px" }}
          />
        </span>
      )}

      {definition.type === "font" && (
        <span
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-[10px]"
          style={{ fontFamily: currentValue }}
        >
          Aa
        </span>
      )}

      {definition.type === "shadow" && (
        <span className="shrink-0 w-7 h-7 flex items-center justify-center">
          <span
            className="block w-5 h-5 rounded-md bg-[var(--color-card)]"
            style={{ boxShadow: currentValue }}
          />
        </span>
      )}

      {/* Label + value */}
      <div className="flex-1 min-w-0">
        <span className="text-[var(--text-sm)] font-medium text-[var(--color-foreground)] block truncate">
          {definition.label}
        </span>
        <span className="text-[10px] text-[var(--color-muted-foreground)] font-mono block truncate">
          {currentValue}
        </span>
      </div>

      {/* Reset per-token */}
      {isModified && (
        <button
          type="button"
          onClick={() => onReset(definition.variable)}
          className="shrink-0 p-1 rounded hover:bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
          style={{ transitionDuration: "var(--duration-fast)" }}
          aria-label={`Reset ${definition.label} to default`}
        >
          <RotateCcw size={14} />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function LiveTokenPreview({ tokenValues, onTokenChange, onResetAll }: LiveTokenPreviewProps) {
  const [defaults, setDefaults] = useState<Map<string, string>>(new Map());
  const [overrides, setOverrides] = useState<Map<string, string>>(new Map());
  const [expandedGroups, setExpandedGroups] = useState<Set<TokenGroup>>(new Set(["colors"]));
  const overridesRef = useRef(overrides);
  const isScopedPreview = Boolean(tokenValues);

  // Sync ref in effect to avoid concurrent mode issues
  useEffect(() => {
    overridesRef.current = overrides;
  }, [overrides]);

  // Cleanup CSS variable overrides on unmount to prevent token corruption
  useEffect(() => {
    if (isScopedPreview) {
      return;
    }

    return () => {
      for (const [variable] of overridesRef.current) {
        document.documentElement.style.removeProperty(variable);
      }
    };
  }, [isScopedPreview]);

  // Capture defaults on mount
  useEffect(() => {
    const map = new Map<string, string>();
    for (const token of TOKEN_CATALOG) {
      if (tokenValues?.[token.variable]) {
        map.set(token.variable, tokenValues[token.variable]!);
      } else {
        map.set(token.variable, getComputedTokenValue(token.variable));
      }
    }
    setDefaults(map);
  }, [tokenValues]);

  const handleTokenChange = useCallback((variable: string, value: string) => {
    if (!isScopedPreview) {
      document.documentElement.style.setProperty(variable, value);
    }

    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(variable, value);
      return next;
    });
    onTokenChange?.(variable, value);
  }, [isScopedPreview, onTokenChange]);

  const handleReset = useCallback((variable: string) => {
    if (!isScopedPreview) {
      document.documentElement.style.removeProperty(variable);
    }

    setOverrides((prev) => {
      const next = new Map(prev);
      next.delete(variable);
      return next;
    });
    const restored = tokenValues?.[variable] ?? defaults.get(variable) ?? getComputedTokenValue(variable);
    onTokenChange?.(variable, restored);
  }, [defaults, isScopedPreview, onTokenChange, tokenValues]);

  const handleResetAll = useCallback(() => {
    setOverrides((prev) => {
      if (!isScopedPreview) {
        for (const [variable] of prev) {
          document.documentElement.style.removeProperty(variable);
        }
      }
      return new Map();
    });
    onResetAll?.();
  }, [isScopedPreview, onResetAll]);

  const toggleGroup = useCallback((group: TokenGroup) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }, []);

  const grouped = useMemo(() => {
    const result = new Map<TokenGroup, readonly TokenDefinition[]>();
    for (const group of GROUP_ORDER) {
      result.set(group, TOKEN_CATALOG.filter((t) => t.group === group));
    }
    return result;
  }, []);

  const modifiedCount = overrides.size;

  return (
    <div className="flex flex-col h-full border-l border-[var(--color-border)] bg-[var(--color-card)]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
        <div className="flex items-center gap-2">
          <Palette size={16} className="text-[var(--color-primary)]" />
          <span className="text-[var(--text-sm)] font-semibold text-[var(--color-foreground)]">
            Design Tokens
          </span>
          {modifiedCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--color-primary)] text-[var(--color-primary-foreground)] font-medium">
              {modifiedCount}
            </span>
          )}
        </div>
        {modifiedCount > 0 && (
          <button
            type="button"
            onClick={handleResetAll}
            className="flex items-center gap-1 text-[var(--text-xs)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
            style={{ transitionDuration: "var(--duration-fast)" }}
          >
            <RotateCcw size={12} />
            Reset All
          </button>
        )}
      </div>

      {/* Scrollable token groups */}
      <div className="flex-1 overflow-y-auto">
        {GROUP_ORDER.map((group) => {
          const tokens = grouped.get(group);
          if (!tokens || tokens.length === 0) return null;
          const meta = GROUP_META[group];
          const Icon = meta.icon;
          const isExpanded = expandedGroups.has(group);
          const groupModifiedCount = tokens.filter((t) => overrides.has(t.variable)).length;

          return (
            <div key={group} className="border-b border-[var(--color-border)]">
              <button
                type="button"
                onClick={() => toggleGroup(group)}
                className="flex items-center justify-between w-full px-4 py-2.5 text-left hover:bg-[var(--color-surface)] transition-colors"
                style={{ transitionDuration: "var(--duration-fast)" }}
                aria-expanded={isExpanded}
              >
                <div className="flex items-center gap-2">
                  <Icon size={14} className="text-[var(--color-muted-foreground)]" />
                  <span className="text-[var(--text-sm)] font-medium text-[var(--color-foreground)]">
                    {meta.label}
                  </span>
                  <span className="text-[10px] text-[var(--color-muted-foreground)]">
                    {tokens.length}
                  </span>
                  {groupModifiedCount > 0 && (
                    <span className="text-[10px] px-1 py-0.5 rounded bg-[var(--color-accent-action-light)] text-[var(--color-accent-action)] font-medium">
                      {groupModifiedCount} changed
                    </span>
                  )}
                </div>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  className="text-[var(--color-muted-foreground)] transition-transform"
                  style={{
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transitionDuration: "var(--duration-fast)",
                  }}
                >
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </button>

              {isExpanded && (
                <div className="px-2 pb-2">
                  {tokens.map((token) => {
                    const current = overrides.get(token.variable)
                      ?? tokenValues?.[token.variable]
                      ?? defaults.get(token.variable)
                      ?? getComputedTokenValue(token.variable);
                    const defaultVal = defaults.get(token.variable) ?? "";

                    return (
                      <TokenRow
                        key={token.variable}
                        definition={token}
                        currentValue={current}
                        defaultValue={defaultVal}
                        isModified={overrides.has(token.variable)}
                        onValueChange={handleTokenChange}
                        onReset={handleReset}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export type { TokenDefinition, TokenGroup, TokenSnapshot };
